import { useMemo } from 'react';
import { t } from '@app/i18n';
import { useLocale } from '@app/i18n/useLocale';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type {
  ChannelsFormValues,
  DiscoveryFormValues,
  TenantFormValues,
} from './TenantSettingsModel';


// ---------------------------------------------------------------- card Tenant (T425)

/**
 * Cliente obrigatório; rótulo do tenant no enum; trust domain no `pattern` do contrato
 * (`^[a-z0-9.-]+$`) — é o mesmo que o backend confere, e apontar o campo aqui
 * evita mandar ao middleware um valor que volta como `valor_invalido` genérico.
 * Vazio passa: o trust domain pode ainda não ter sido definido.
 */
export function tenantSchema(): yup.ObjectSchema<TenantFormValues> {
  return yup.object({
    cliente: yup
      .string()
      .trim()
      .required(t('tenantSettings.tenant.form.validation.clientRequired')),
    label: yup
      .mixed<TenantFormValues['label']>()
      .oneOf(
        ['producao', 'homologacao'],
        t('tenantSettings.tenant.form.validation.labelInvalid'),
      )
      .required(t('tenantSettings.tenant.form.validation.labelRequired')),
    // A regra do trust domain saiu com o campo (S-b (d)). Ela não morreu: o mesmo
    // charset, com o mesmo código `trustDomainCharset`, vive no cadastro de sites —
    // que é quem grava o valor.
  });
}

export function useTenantValidation() {
  const locale = useLocale();
  // O lint não enxerga a leitura do idioma feita pelo `t()` lá dentro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => tenantSchema(), [locale]);
  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
  };
}

// ---------------------------------------------------------------- canais (T426)

const optionalURL = (mensagem: string) =>
  yup
    .string()
    .trim()
    .defined()
    .test('url', mensagem, (valor) => {
      if (!valor) return true;
      try {
        return Boolean(new URL(valor));
      } catch {
        return false;
      }
    });

/** Tudo opcional — os canais são informação para documentação (FR-116). */
export function channelsSchema(): yup.ObjectSchema<ChannelsFormValues> {
  return yup.object({
    uraIssuer: optionalURL(t('tenantSettings.channels.form.validation.ivrIssuerUrl')),
    uraJwksUrl: optionalURL(t('tenantSettings.channels.form.validation.ivrJwksUrl')),
    uraMapeamento: yup.string().trim().defined(),
    ssoIssuer: optionalURL(t('tenantSettings.channels.form.validation.ssoIssuerUrl')),
    ssoMapeamento: yup.string().trim().defined(),
  });
}

export function useChannelsValidation() {
  const locale = useLocale();
  // O lint não enxerga a leitura do idioma feita pelo `t()` lá dentro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => channelsSchema(), [locale]);
  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
  };
}

// ---------------------------------------------------------------- discovery (T426)

/** Intervalo inteiro ≥ 1 — o piso do contrato (`minimum: 1`) e do worker. */
export function discoverySchema(): yup.ObjectSchema<DiscoveryFormValues> {
  return yup.object({
    ativo: yup.boolean().defined(),
    intervaloMin: yup
      .number()
      .typeError(t('tenantSettings.discovery.form.validation.intervalType'))
      .integer(t('tenantSettings.discovery.form.validation.intervalInteger'))
      .min(1, t('tenantSettings.discovery.form.validation.intervalMin'))
      .required(t('tenantSettings.discovery.form.validation.intervalRequired')),
  });
}

export function useDiscoveryValidation() {
  const locale = useLocale();
  // O lint não enxerga a leitura do idioma feita pelo `t()` lá dentro.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => discoverySchema(), [locale]);
  return {
    resolver: yupResolver(schema),
    mode: 'onChange' as const,
  };
}
